import { z } from 'zod';

import { positiveIntSchema } from './common';
import { phaseIdSchema, roundKeySchema } from './ids';

export const scoringModeSchema = z.enum(['legs', 'sets']);
export type ScoringMode = z.infer<typeof scoringModeSchema>;

/**
 * How a single match is played.
 *
 * `bestOf` is deliberately not restricted to odd numbers: an even value makes a
 * draw possible, which a group phase may well want (see `pointsForDraw`). Which
 * value applies to which round is settings, never code.
 */
export const matchRuleSetSchema = z
  .object({
    scoring: scoringModeSchema,
    bestOf: positiveIntSchema.max(99),
    /** Required when scoring by sets: how many legs decide one set. */
    legsPerSet: positiveIntSchema.max(99).optional(),
  })
  .superRefine((rules, ctx) => {
    if (rules.scoring === 'sets' && rules.legsPerSet === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'Bei Satzmodus muss die Anzahl Legs pro Satz angegeben werden',
        path: ['legsPerSet'],
      });
    }
  });

export type MatchRuleSet = z.infer<typeof matchRuleSetSchema>;

/**
 * Match rules for the whole tournament, resolved by specificity:
 * round override → phase override → default.
 *
 * "Gruppenphase Best of 3, Achtelfinale Best of 5, Halbfinale Best of 7,
 * Finale Best of 9" is expressed entirely by filling `byRound`.
 */
export const matchRulesSettingsSchema = z.object({
  default: matchRuleSetSchema,
  byPhase: z.record(phaseIdSchema, matchRuleSetSchema),
  byRound: z.record(roundKeySchema, matchRuleSetSchema),
});

export type MatchRulesSettings = z.infer<typeof matchRulesSettingsSchema>;
